import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { hostUI } from '../../../../configuraciones';

import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GrassIcon from '@mui/icons-material/Grass';
import ScienceIcon from '@mui/icons-material/Science';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const num = Number(bytes);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch (_) {
    return String(dateStr);
  }
};

const ModalDocumentosCoccion = ({
  isOpen,
  onClose,
  coccion,
  onDocumentUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [subiendoTipo, setSubiendoTipo] = useState(null); // 'guia_maguey' | 'informe_art'
  const [docs, setDocs] = useState({ guia_maguey: null, informe_art: null });

  const inputGuiaRef = useRef(null);
  const inputArtRef = useRef(null);

  const idCoccion = coccion?.rawId || coccion?.idBitacoraCoccion || coccion?.id_bitacora_coccion || (typeof coccion?.dbId === 'number' ? coccion.dbId : null) || (typeof coccion?.id === 'number' ? coccion.id : null) || coccion?.coccion?.rawId || coccion?.coccion?.idBitacoraCoccion || coccion?.coccion?.id_bitacora_coccion || coccion?.coccion?.dbId || (typeof coccion?.coccion?.id === 'number' ? coccion.coccion.id : null) || (Number(coccion?.id) > 0 ? Number(coccion?.id) : null) || (Number(coccion?.rawId) > 0 ? Number(coccion?.rawId) : null) || coccion?.dbId || coccion?.no_produccion || coccion?.noProduccion || coccion?.coccion?.no_produccion || coccion?.coccion?.noProduccion || coccion?.id;

  // Consultar estado actual de los documentos
  const fetchDocs = async () => {
    if (!idCoccion) return;
    setLoading(true);
    try {
      const res = await axios.get(`${hostUI}bitacora-produccion/coccion/${idCoccion}/documentos`);
      if (res.data?.data) {
        setDocs({
          guia_maguey: res.data.data.guia_maguey || null,
          informe_art: res.data.data.informe_art || null
        });
      }
    } catch (error) {
      console.error("Error al obtener documentos:", error);
      if (coccion) {
        setDocs({
          guia_maguey: coccion.doc_guia_maguey || coccion.documentosMap?.guia_maguey || null,
          informe_art: coccion.doc_informe_art || coccion.documentosMap?.informe_art || null
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && idCoccion) {
      fetchDocs();
    }
  }, [isOpen, idCoccion]);

  if (!isOpen || !coccion) return null;

  const noProduccion = coccion.no_produccion || coccion.noProduccion || `ID #${idCoccion}`;
  const predio = coccion.predio || coccion.guia_predio || coccion.unidadProduccion || "Instalación de Producción";

  const handleSelectFile = (tipo) => {
    if (tipo === 'guia_maguey' && inputGuiaRef.current) {
      inputGuiaRef.current.value = null;
      inputGuiaRef.current.click();
    } else if (tipo === 'informe_art' && inputArtRef.current) {
      inputArtRef.current.value = null;
      inputArtRef.current.click();
    }
  };

  const handleFileChange = async (e, tipo) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 30 * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo demasiado grande',
        text: 'El documento excede el tamaño máximo permitido de 30 MB.',
        confirmButtonColor: '#4A5D23'
      });
      return;
    }

    const tipoLabel = tipo === 'guia_maguey' ? 'Guía de Maguey' : 'Informe de ART';
    const formData = new FormData();
    formData.append('tipo_documento', tipo);
    formData.append('archivo', file);

    setSubiendoTipo(tipo);
    try {
      const res = await axios.post(`${hostUI}bitacora-produccion/coccion/${idCoccion}/documentos`, formData);

      const nuevoDoc = res.data?.data;
      setDocs(prev => ({ ...prev, [tipo]: nuevoDoc }));

      Swal.fire({
        icon: 'success',
        title: '¡Documento Cargado!',
        text: `${tipoLabel} se ha guardado correctamente.`,
        timer: 2000,
        showConfirmButton: false
      });

      if (onDocumentUpdated) {
        try {
          onDocumentUpdated({ id_bitacora_coccion: idCoccion, tipo, doc: nuevoDoc });
        } catch (cbErr) {
          console.warn("Advertencia en callback onDocumentUpdated:", cbErr);
        }
      }
    } catch (error) {
      console.error("Error al subir archivo:", error);
      const msg = error.response?.data?.message || "Ocurrió un error al cargar el archivo.";
      Swal.fire({
        icon: 'error',
        title: 'Error al subir',
        text: msg,
        confirmButtonColor: '#DC2626'
      });
    } finally {
      setSubiendoTipo(null);
    }
  };

  const handleVerDoc = (tipo) => {
    const url = `${hostUI}bitacora-produccion/coccion/${idCoccion}/documentos/${tipo}/ver`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEliminarDoc = async (tipo) => {
    const tipoLabel = tipo === 'guia_maguey' ? 'Guía de Maguey' : 'Informe de ART';
    const result = await Swal.fire({
      icon: 'question',
      title: '¿Eliminar documento?',
      text: `Se eliminará permanentemente ${tipoLabel}.`,
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setSubiendoTipo(tipo);
    try {
      await axios.delete(`${hostUI}bitacora-produccion/coccion/${idCoccion}/documentos/${tipo}`);
      setDocs(prev => ({ ...prev, [tipo]: null }));

      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: `${tipoLabel} ha sido eliminado.`,
        timer: 1800,
        showConfirmButton: false
      });

      if (onDocumentUpdated) {
        try {
          onDocumentUpdated({ id_bitacora_coccion: idCoccion, tipo, doc: null });
        } catch (cbErr) {
          console.warn("Advertencia en callback onDocumentUpdated:", cbErr);
        }
      }
    } catch (error) {
      console.error("Error al eliminar documento:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: error.response?.data?.message || "No se pudo eliminar el documento.",
        confirmButtonColor: '#DC2626'
      });
    } finally {
      setSubiendoTipo(null);
    }
  };

  const renderCardDoc = (tipo, titulo, IconComponent, subtitulo, doc) => {
    const isUploaded = Boolean(doc);
    const isSubiendo = subiendoTipo === tipo;

    return (
      <div
        className="doc-item-card"
        style={{
          border: `1px solid ${isUploaded ? '#10B981' : '#E5E7EB'}`,
          backgroundColor: isUploaded ? 'rgba(16, 185, 129, 0.04)' : '#F9FAFB',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                backgroundColor: isUploaded ? '#D1FAE5' : '#E5E7EB',
                color: isUploaded ? '#059669' : '#6B7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IconComponent style={{ fontSize: 24 }} />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1F2937' }}>
                {titulo}
              </h4>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6B7280' }}>
                {subtitulo}
              </p>
            </div>
          </div>

          {isUploaded ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: '#ECFDF5',
                color: '#059669',
                border: '1px solid #A7F3D0'
              }}
            >
              <CheckCircleIcon style={{ fontSize: 14 }} />
              Cargado
            </span>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
                border: '1px solid #E5E7EB'
              }}
            >
              <HourglassEmptyIcon style={{ fontSize: 14 }} />
              Pendiente
            </span>
          )}
        </div>

        {isUploaded ? (
          <div
            style={{
              padding: '10px 14px',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              marginBottom: '12px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                <PictureAsPdfIcon style={{ fontSize: 20, color: '#DC2626' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }} title={doc.nombre_original || doc.nombre_archivo}>
                  {doc.nombre_original || doc.nombre_archivo}
                </span>
              </div>
              <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {formatFileSize(doc.tamano_bytes)}
              </span>
            </div>
            {doc.fecha_subida && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                Actualizado: {formatDate(doc.fecha_subida)}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: '14px',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: '1.5px dashed #D1D5DB',
              textAlign: 'center',
              marginBottom: '12px',
              color: '#6B7280'
            }}
          >
            <UploadFileIcon style={{ fontSize: 26, color: '#9CA3AF', marginBottom: '4px' }} />
            <div style={{ fontSize: '12.5px', fontWeight: 500 }}>No se ha cargado este documento todavía.</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Formatos aceptados: PDF o imágenes (JPG, PNG). Máx. 30 MB.</div>
          </div>
        )}

        {/* Botones de acción */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {isUploaded && (
            <>
              <button
                type="button"
                onClick={() => handleVerDoc(tipo)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  backgroundColor: '#FFFFFF',
                  color: '#2563EB',
                  border: '1px solid #BFDBFE',
                  borderRadius: '6px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                title="Abrir documento en una nueva pestaña"
              >
                <VisibilityIcon style={{ fontSize: 16 }} />
                Ver
              </button>

              <button
                type="button"
                onClick={() => handleSelectFile(tipo)}
                disabled={isSubiendo}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  backgroundColor: '#FFFFFF',
                  color: '#4B5563',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: isSubiendo ? 'not-allowed' : 'pointer'
                }}
                title="Seleccionar otro archivo para reemplazar el existente"
              >
                <SyncIcon style={{ fontSize: 16 }} />
                {isSubiendo ? 'Reemplazando...' : 'Reemplazar'}
              </button>

              <button
                type="button"
                onClick={() => handleEliminarDoc(tipo)}
                disabled={isSubiendo}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '6px 10px',
                  backgroundColor: '#FFFFFF',
                  color: '#DC2626',
                  border: '1px solid #FECACA',
                  borderRadius: '6px',
                  fontSize: '12.5px',
                  cursor: isSubiendo ? 'not-allowed' : 'pointer'
                }}
                title="Eliminar documento"
              >
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
              </button>
            </>
          )}

          {!isUploaded && (
            <button
              type="button"
              onClick={() => handleSelectFile(tipo)}
              disabled={isSubiendo}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 16px',
                backgroundColor: '#4A5D23',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: isSubiendo ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <UploadFileIcon style={{ fontSize: 17 }} />
              {isSubiendo ? 'Subiendo...' : `Subir ${titulo}`}
            </button>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <div
      className="modal-backdrop-custom"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="modal-dialog-custom"
        style={{
          width: '100%',
          maxWidth: '540px',
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#111827' }}>
              Documentos de Cocción
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#6B7280' }}>
              Lote: <strong style={{ color: '#4A5D23' }}>{noProduccion}</strong> • {predio}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9CA3AF',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Inputs ocultos */}
        <input
          type="file"
          ref={inputGuiaRef}
          style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.jfif"
          onChange={(e) => handleFileChange(e, 'guia_maguey')}
        />
        <input
          type="file"
          ref={inputArtRef}
          style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.jfif"
          onChange={(e) => handleFileChange(e, 'informe_art')}
        />

        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
            Adjunte la Guía de Maguey y el Informe de ART correspondientes a este lote de cocción. Si ya han sido cargados, puede visualizarlos o reemplazarlos.
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#6B7280' }}>
              <div style={{ fontSize: '13px' }}>Cargando documentos de la cocción...</div>
            </div>
          ) : (
            <>
              {renderCardDoc(
                'guia_maguey',
                'Guía de Maguey',
                GrassIcon,
                'Acreditación de origen, predio y traslado de agave',
                docs.guia_maguey
              )}

              {renderCardDoc(
                'informe_art',
                'Informe de ART',
                ScienceIcon,
                'Resultado de laboratorio con porcentaje de Azúcares Reductores Totales',
                docs.informe_art
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            backgroundColor: '#F9FAFB',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 18px',
              backgroundColor: '#E5E7EB',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalDocumentosCoccion;
